// src/views/admin/inventory/components/InventoryAccordion.js
import React, { useEffect, useMemo, useState } from "react";
import InventoryProjectionGraph from "./InventoryProjectionGraph";

/* =========================
   Utils
========================= */

function currencySymbol(code) {
  const c = (code || "").toUpperCase();
  if (c === "GBP") return "£";
  if (c === "USD") return "$";
  if (c === "EUR") return "€";
  if (c === "KRW") return "₩";
  return c ? `${c} ` : "";
}

function periodLabel(period) {
  if (period === "weekly") return "weeks";
  if (period === "monthly") return "months";
  return "days";
}

function getStockStatus(days, period) {
  const unit = periodLabel(period);

  if (days == null) {
    return {
      label: "Unknown",
      color: "bg-neutral-700/30 text-gray-300 border-neutral-600/50",
    };
  }
  if (days <= 1) {
    return {
      label: `Critical · ${days} ${unit}`,
      color: "bg-red-600/20 text-red-300 border-red-500/40",
    };
  }
  if (days <= 3) {
    return {
      label: `Low · ${days} ${unit}`,
      color: "bg-yellow-600/20 text-yellow-200 border-yellow-500/40",
    };
  }
  return {
    label: `Healthy · ${days} ${unit}`,
    color: "bg-green-600/20 text-green-200 border-green-500/40",
  };
}

/* =========================
   Forecast-based reorder
========================= */

function computeSuggestedPacks(item, targetIntervals = 2) {
  const packSize = Number(item.purchase_quantity);
  const remaining = Number(item.estimated_remaining || 0);

  if (!packSize || !item.curve?.consumption?.length) return null;

  const slice = item.curve.consumption.slice(0, targetIntervals);
  const targetQty = slice.reduce((s, c) => s + Number(c.value || 0), 0);
  const reorderQty = Math.max(0, targetQty - remaining);
  const packs = reorderQty / packSize;

  return { packs, targetIntervals };
}

/* =========================
   Status bar
========================= */

function StatusBar({ item }) {
  const status = getStockStatus(item.days_of_cover, item.period);

  return (
    <div className={`flex justify-between items-center rounded-xl border px-5 py-4 ${status.color}`}>
      <div>
        <p className="text-sm font-semibold">{status.label}</p>
        <p className="text-xs text-gray-400">
          Run out: {item.runout_date ?? "—"}
        </p>
      </div>

      <div className="text-right">
        <p className="text-xs text-gray-400">Current stock</p>
        <p className="text-lg font-bold">
          {Number(item.estimated_remaining || 0).toLocaleString()} {item.display_unit}
        </p>
      </div>
    </div>
  );
}

/* =========================
   🔥 MAIN ACTION CARD
========================= */

function ReorderAdjustCard({ item, onSave }) {
  const packSize = Number(item.purchase_quantity || 0);
  const hasPack = packSize > 0;

  // ✅ 분리
  const countedUnits = Number(item.current_quantity || 0);       // 실측
  const estimatedUnits = Number(item.estimated_remaining || 0);  // 예측

  const countedPacks = hasPack ? countedUnits / packSize : null;
  const estimatedPacks = hasPack ? estimatedUnits / packSize : null;

  const suggested = useMemo(() => computeSuggestedPacks(item, 2), [item]);
  const suggestedPacks = suggested?.packs;

  const [mode, setMode] = useState("delta"); // delta | absolute
  const [value, setValue] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (hasPack && suggestedPacks != null) {
      setMode("delta");
      setValue(Number(suggestedPacks.toFixed(1)));
    } else {
      setValue(0);
    }
  }, [item.id, hasPack, suggestedPacks]);

  /* ✅ 항상 "실측값 기준" */
  const previewUnits = useMemo(() => {
    const v = Number(value);
    if (!Number.isFinite(v)) return null;

    const deltaUnits = hasPack ? v * packSize : v;

    return mode === "delta"
      ? Math.max(0, countedUnits + deltaUnits)
      : Math.max(0, deltaUnits);
  }, [value, mode, countedUnits, hasPack, packSize]);

  const previewPacks =
    hasPack && previewUnits != null ? previewUnits / packSize : null;

  const apply = async () => {
    const backendMode = mode === "delta" ? "add" : "set";

    setSaving(true);
    try {
      await onSave(item.id, {
        action: "adjust_stock",
        mode: backendMode,
        unit: hasPack ? "pack" : "unit",
        value: Number(value),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-neutral-900 border border-yellow-500/40 rounded-2xl p-6 space-y-5">

      {/* HEADER */}
      <div className="flex justify-between items-start gap-6">
  <div>
    <p className="text-sm text-gray-400">Reorder & quick update</p>

    {/* 🔥 PRIMARY: ESTIMATED */}
    <p className="text-4xl font-extrabold text-white leading-tight mt-2">
      Estimated now:&nbsp;
      {hasPack
        ? `${estimatedPacks.toFixed(1)} packs`
        : `${estimatedUnits.toLocaleString()} ${item.display_unit}`}
    </p>

    {/* 🔽 reference info */}
    <div className="mt-3 space-y-1 text-sm text-gray-500">
      <p className="text-lg font-semibold text-gray-200">
        Last counted:&nbsp;
        {hasPack
          ? `${countedPacks.toFixed(1)} packs`
          : `${countedUnits.toLocaleString()} ${item.display_unit}`}
      </p>

      <p>
        Counted at:&nbsp;
        {item.last_counted_at
          ? new Date(item.last_counted_at).toLocaleString()
          : "—"}
      </p>
    </div>
  </div>

  {hasPack && suggestedPacks != null && (
    <button
      onClick={() => {
        setMode("delta");
        setValue(Number(suggestedPacks.toFixed(1)));
      }}
      className="
        h-fit
        px-5 py-3
        rounded-lg
        bg-yellow-600/20 text-yellow-300
        border border-yellow-500/40
        hover:bg-yellow-600/30
        text-sm font-semibold
      "
    >
      Use suggestion
    </button>
  )}
</div>

      {/* SUGGESTED */}
      {hasPack && suggestedPacks != null && (
        <div className="text-sm text-gray-400">
          Suggested (next {suggested.targetIntervals} {periodLabel(item.period)}):{" "}
          <span className="text-yellow-300 font-semibold">
            {suggestedPacks.toFixed(1)} packs
          </span>
        </div>
      )}

      {/* CONTROLS */}
      <div className="flex items-end gap-3">
        <select
          value={mode}
          onChange={(e) => setMode(e.target.value)}
          className="bg-neutral-800 border border-neutral-600 rounded px-3 py-2 text-sm"
        >
          <option value="delta">Add</option>
          <option value="absolute">Set</option>
        </select>

        <button
          onClick={() => setValue((v) => Math.max(0, Number(v) - 1))}
          className="px-3 py-2 bg-neutral-800 border border-neutral-600 rounded text-lg"
          disabled={saving}
        >
          −
        </button>

        <input
          type="number"
          step={hasPack ? "0.1" : "1"}
          value={value}
          onChange={(e) => setValue(e.target.value === "" ? "" : Number(e.target.value))}
          className="w-28 bg-neutral-800 border border-neutral-600 rounded px-3 py-2 text-center text-lg"
          disabled={saving}
        />

        <button
          onClick={() => setValue((v) => Number(v) + 1)}
          className="px-3 py-2 bg-neutral-800 border border-neutral-600 rounded text-lg"
          disabled={saving}
        >
          +
        </button>

        <span className="text-sm text-gray-400">
          {hasPack ? "packs" : item.display_unit}
        </span>

        <button
          onClick={apply}
          disabled={saving || !Number.isFinite(Number(value))}
          className="ml-auto px-4 py-2.5 rounded-md bg-yellow-500 hover:bg-yellow-400 text-black text-sm font-semibold"
        >
          {saving ? "Applying..." : "Apply"}
        </button>
      </div>

      {/* PREVIEW */}
      {previewUnits != null && (
        <div className="mt-2 px-4 py-2 rounded-md bg-neutral-800 text-sm text-gray-300">
          After update:&nbsp;
          <span className="font-semibold text-gray-100">
            {hasPack
              ? `${previewPacks.toFixed(1)} packs`
              : `${previewUnits.toLocaleString()} ${item.display_unit}`}
          </span>
        </div>
      )}
    </div>
  );
}
/* =========================
   MAIN
========================= */

export default function InventoryAccordion({ item, onSave }) {
  return (
    <div className="space-y-6 p-6 bg-neutral-950 rounded-xl">

      {/* STATUS */}
      <StatusBar item={item} />

      {/* GRAPH + INFO */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* GRAPH */}
        {item?.curve && (
          <div className="lg:col-span-3 bg-neutral-900 border border-neutral-700 rounded-xl p-4">
            <p className="text-sm text-gray-400 mb-2">Inventory projection</p>
            <InventoryProjectionGraph
              curve={item.curve}
              interval={item.period}
              unit={item.display_unit}
              height={220}
            />
          </div>
        )}

        {/* INFO */}
        <div className="lg:col-span-2 bg-neutral-900 border border-neutral-700 rounded-xl p-4 space-y-3">
          <div className="text-sm text-gray-400">
            Forecast cost
            <div className="text-xl font-semibold text-gray-100">
              {currencySymbol(item.currency)}
              {Number(item.forecast_cost || 0).toFixed(2)}
            </div>
          </div>
        </div>
      </div>

      {/* 🔥 MAIN ACTION */}
      <ReorderAdjustCard item={item} onSave={onSave} />
    </div>
  );
}